<template>
  <ServicePageLayout
    title="LINE Message"
    subtitle="ดู Message API Quota และ log จาก lineEvent ตาม lineBotId ของอาคาร"
    icon="mdi-chat-processing-outline"
    breadcrumb="Operations"
  >
    <v-row class="mt-2">
      <v-col cols="12" class="text-right">
        <v-btn color="primary" :loading="loading" @click="load">
          <v-icon left>mdi-refresh</v-icon>Refresh
        </v-btn>
      </v-col>
    </v-row>

    <v-row>
      <v-col v-for="item in quotas" :key="item.buildingId" cols="12" md="6" lg="4">
        <v-card class="quota-card">
          <v-card-text class="quota-card-header pb-2">
            <div class="quota-card-title">{{ item.buildingName }}</div>
            <div class="quota-card-subtitle text-caption grey--text">
              Bot ID: {{ item.botId || '-' }}
            </div>
          </v-card-text>
          <v-card-text class="pt-0">
            <v-alert v-if="item.error" type="warning" dense text class="mb-0">
              {{ item.error }}
            </v-alert>
            <template v-else>
              <div class="d-flex justify-space-between mb-2">
                <span class="grey--text">Quota type</span>
                <span>{{ quotaTypeLabel(item.quota) }}</span>
              </div>
              <div class="d-flex justify-space-between mb-2">
                <span class="grey--text">Limit</span>
                <span>{{ formatNumber(item.quota && item.quota.value) }}</span>
              </div>
              <div class="d-flex justify-space-between mb-2">
                <span class="grey--text">Used</span>
                <span>{{ formatNumber(item.consumption && item.consumption.totalUsage) }}</span>
              </div>
              <div class="d-flex justify-space-between mb-2">
                <span class="grey--text">Remaining</span>
                <span :class="remainingClass(item)">{{ formatRemaining(item) }}</span>
              </div>
              <v-progress-linear
                v-if="usagePercent(item) !== null"
                :value="usagePercent(item)"
                :color="usageColor(item)"
                height="8"
                rounded
                class="mt-2"
              />
            </template>
          </v-card-text>
        </v-card>
      </v-col>
      <v-col v-if="!loading && quotas.length === 0" cols="12">
        <v-alert type="info" text>
          ไม่พบอาคารที่มี botId ในระบบ กรุณาตั้งค่า botId ในตาราง buildings
        </v-alert>
      </v-col>
    </v-row>

    <v-card class="mt-4">
      <v-card-title>LINE Event Log</v-card-title>
      <v-card-text>
        <v-row>
          <v-col cols="12" md="4">
            <v-text-field
              v-model="search"
              label="Search log"
              prepend-inner-icon="mdi-magnify"
              dense
              solo
              clearable
            />
          </v-col>
        </v-row>
        <v-data-table
          :headers="eventHeaders"
          :items="events"
          :loading="loading"
          :search="search"
          dense
          item-key="rowKey"
          :items-per-page="25"
          class="elevation-0 event-log-table"
        >
          <template v-slot:item.eventTime="{ item }">
            {{ formatDateTime(item.eventTime) }}
          </template>
        </v-data-table>
      </v-card-text>
    </v-card>
  </ServicePageLayout>
</template>

<script>
import ServicePageLayout from '@/components/layout/ServicePageLayout.vue'
import { getLineQuota } from '@/api/'
import dayjs from 'dayjs'

export default {
  name: 'LineQuotaView',
  components: { ServicePageLayout },
  data() {
    return {
      loading: false,
      search: '',
      quotas: [],
      events: [],
      eventHeaders: [
        { text: 'Time', value: 'eventTime' },
        { text: 'LINE Bot ID', value: 'lineBotId' },
        { text: 'Event', value: 'eventType' },
        { text: 'Source', value: 'sourceType' },
        { text: 'Group ID', value: 'groupId' },
        { text: 'User ID', value: 'userId' },
        { text: 'Message Type', value: 'messageType' },
        { text: 'Message', value: 'messageText', sortable: false }
      ]
    }
  },
  computed: {
    buildingId() {
      return this.$store.state.User.building?.BuildingId
    }
  },
  watch: {
    buildingId: {
      immediate: true,
      handler() {
        this.load()
      }
    }
  },
  methods: {
    load() {
      if (!this.buildingId) return
      this.loading = true
      getLineQuota(
        { buildingId: this.buildingId, limit: 200 },
        (res) => {
          this.quotas = res.data?.quotas || []
          this.events = res.data?.events || []
          this.loading = false
        },
        () => {
          this.quotas = []
          this.events = []
          this.loading = false
        }
      )
    },
    quotaTypeLabel(quota) {
      if (!quota) return '-'
      if (quota.type === 'none') return 'Unlimited'
      if (quota.type === 'limited') return 'Limited'
      return quota.type || '-'
    },
    formatNumber(value) {
      if (value === null || value === undefined || value === '') return '-'
      return Number(value).toLocaleString()
    },
    formatRemaining(item) {
      if (item.remaining !== null && item.remaining !== undefined) {
        return this.formatNumber(item.remaining)
      }
      if (item.quota?.type === 'none') return 'Unlimited'
      return '-'
    },
    usagePercent(item) {
      if (!item.quota || item.quota.type !== 'limited' || !item.quota.value) return null
      const used = item.consumption?.totalUsage ?? 0
      return Math.min(100, Math.round((used / item.quota.value) * 100))
    },
    usageColor(item) {
      const percent = this.usagePercent(item)
      if (percent === null) return 'primary'
      if (percent >= 90) return 'error'
      if (percent >= 70) return 'warning'
      return 'success'
    },
    remainingClass(item) {
      const percent = this.usagePercent(item)
      if (percent === null) return ''
      if (percent >= 90) return 'error--text font-weight-bold'
      if (percent >= 70) return 'warning--text'
      return 'success--text'
    },
    formatDateTime(value) {
      if (!value) return '-'
      const n = Number(value)
      const d = !Number.isNaN(n) && n > 0 ? dayjs(n) : dayjs(value)
      return d.isValid() ? d.format('YYYY-MM-DD HH:mm:ss') : String(value)
    }
  }
}
</script>

<style scoped>
.quota-card-header {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.quota-card-title {
  font-size: 1rem;
  font-weight: 500;
  line-height: 1.45;
  white-space: normal;
  word-break: break-word;
}

.quota-card-subtitle {
  line-height: 1.35;
  word-break: break-all;
}

.event-log-table >>> th,
.event-log-table >>> td {
  white-space: normal !important;
  word-break: break-word;
  vertical-align: top;
}
</style>
